import type { EntryPoint, EntryPointComponent } from 'react-relay';
/* eslint-disable eslint-comments/no-restricted-disable */
/* eslint-disable @atlassian/react-entrypoint/no-imports-in-entrypoint-file */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { getProjectKey } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { AnyAri } from '@atlassian/ari/any-ari';
import { NavigationSettingAri } from '@atlassian/ari/navigation/setting';
import type { JswExpandableProjectQueryEntrypointProps } from './JswExpandableProjectQuery.entrypoint.types.tsx';
import parameters, {
	type JswExpandableProjectQuery,
} from './__generated__/JswExpandableProjectQuery.graphql';

type Queries = { projects: JswExpandableProjectQuery };

type JswExpandableProjectQueryExtraProps = {
	entityAri: string;
	ownerAri: string;
	cloudId: string;
};

const NO_OF_BOARDS_TO_FETCH = 25;

type Nav4JSWProjectEntryPointComponent = EntryPointComponent<
	Queries,
	{},
	JswExpandableProjectQueryEntrypointProps,
	JswExpandableProjectQueryExtraProps
>;

export const nav4JSWProjectEntryPoint: EntryPoint<
	Nav4JSWProjectEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<Nav4JSWProjectEntryPointComponent>(
		/* webpackChunkName: "nav4-jsw-project-entry-point" */ './JswExpandableProjectQuery.tsx',
		{
			includeInShellOnlySsrBundle: true,
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: (params: EntryPointRouteParams) => {
		const cloudId = params.tenantContext?.cloudId ?? '';
		const accountId = params.tenantContext?.atlassianAccountId;
		const activationId = params.tenantContext?.activationId ?? '';
		if (!accountId) {
			return {
				extraProps: {
					cloudId: '',
					entityAri: '',
					ownerAri: '',
				},
			};
		}
		let projectKey = '';
		if (params.context?.match.params?.projectKey) {
			projectKey = params.context.match.params.projectKey;
		} else if (params.context?.match.params?.issueKey && fg('move-issue-view-to-entrypoint')) {
			projectKey = getProjectKey(params.context.match, params.context.query);
		}
		const entityAri = fg('navx-3660-fix-l2-l3-aris')
			? NavigationSettingAri.create({
					activationId,
					firstPartyApp: 'jira',
					siteId: cloudId,
					settingId: `project-${projectKey.toLowerCase()}-subcontainer`,
				}).toString()
			: AnyAri.create({
					cloudId,
					resourceType: 'project',
					resourceId: projectKey,
					resourceOwner: 'jira',
				}).toString();
		const ownerAri = IdentityUserAri.create({
			userId: accountId || 'UNKNOWN',
		}).toString();
		// Feature gate variables - original query doesn't have entityAri and ownerAri
		const variables = {
			cloudId,
			projectKey,
			noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
			ownerAri,
			entityAri,
			skipOrderQuery: false,
		};
		return {
			queries: {
				projects: {
					parameters,
					variables,
				},
			},
			extraProps: {
				cloudId,
				entityAri,
				ownerAri,
			},
		};
	},
});
